import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { formatFullDate, getAllGames, getGameById, getTeamById, stageLabel } from "@/utils/nba";
import TeamBadge from "@/components/base/TeamBadge";
import GameCard from "@/components/base/GameCard";
import ScoreChart from "@/pages/detail/components/ScoreChart";
import StatsCompare from "@/pages/detail/components/StatsCompare";
import RosterPanel from "@/pages/detail/components/RosterPanel";
import TimelineFeed from "@/pages/detail/components/TimelineFeed";

const tabs = [
  { key: "chart", label: "比分节奏", icon: "ri-line-chart-line" },
  { key: "stats", label: "技术统计", icon: "ri-bar-chart-2-line" },
  { key: "roster", label: "球员数据", icon: "ri-team-line" },
  { key: "timeline", label: "赛况动态", icon: "ri-live-line" },
];

export default function Detail() {
  const { gameId } = useParams();
  const game = getGameById(gameId);
  const [activeTab, setActiveTab] = useState("chart");
  const { isFavorite, toggleFavorite } = useFavorites();

  const home = getTeamById(game?.homeTeamId);
  const away = getTeamById(game?.awayTeamId);

  useSeo({
    title: game && home && away
      ? `${away.fullName} vs ${home.fullName} - NBA比赛直播详情 - NBA直播吧`
      : "NBA比赛直播详情 - NBA直播吧",
    description: game && home && away
      ? `${away.fullName} 对阵 ${home.fullName} 的${stageLabel(game.stage)}赛事详情，包含实时比分、比分节奏、球队技术统计与球员数据，${formatFullDate(game.date)} 开赛。`
      : "NBA 比赛直播详情页，提供实时比分、技术统计与赛况动态。",
    keywords: "NBA比赛详情,NBA直播,NBA实时比分,NBA技术统计,NBA直播吧",
  });

  if (!game || !home || !away) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-2xl" />
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该场赛事</h1>
        <p className="mt-2 text-sm text-foreground-500">赛事可能已下架或链接有误，返回首页查看今日直播安排。</p>
        <Link to="/" className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const related = getAllGames()
    .filter((item) => item.id !== game.id && (item.date === game.date || item.stage === game.stage))
    .slice(0, 4);

  const favorited = isFavorite(game.id);

  return (
    <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${game.season}`} className="hover:text-primary-700 cursor-pointer">{game.season} 赛季</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900 font-medium">{away.abbreviation} vs {home.abbreviation}</span>
      </nav>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">{stageLabel(game.stage)}</span>
            {game.status === "live" && (
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-dot" />
                直播中 {game.quarter} {game.clock}
              </span>
            )}
            <span className="text-xs text-foreground-500">{formatFullDate(game.date)} {game.time} · {game.arena}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {away.fullName} <span className="text-foreground-400">vs</span> {home.fullName}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => toggleFavorite(game.id)}
          className={`inline-flex items-center justify-center gap-2 rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
            favorited
              ? "border-accent-500 bg-accent-50 text-accent-700"
              : "border-background-300 text-foreground-700 hover:bg-background-100"
          }`}
        >
          <i className={favorited ? "ri-star-fill" : "ri-star-line"} />
          {favorited ? "已收藏本场" : "收藏本场"}
        </button>
      </div>

      {/* 直播视频区 */}
      <div className="relative mb-6 aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur">
            <i className="ri-play-fill text-3xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-background-50">直播视频区（界面演示）</p>
          <p className="mt-1 max-w-md px-4 text-xs text-background-100/70">
            本站为前端演示项目，未接入真实直播流媒体与播放资源，视频区域仅用于展示页面结构。
          </p>
        </div>
        <span className="absolute left-4 top-4 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
          {game.status === "live" ? "LIVE 演示" : game.status === "final" ? "录像演示" : "预告演示"}
        </span>
      </div>

      {/* 实时比分 */}
      <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
        <div className="grid grid-cols-3 items-center gap-3">
          <Link to={`/team/${away.id}`} className="flex flex-col items-center gap-2 cursor-pointer group">
            <TeamBadge team={away} size="lg" />
            <span className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-700 text-center">{away.fullName}</span>
            <span className="text-xs text-foreground-500">客场 · {away.wins}胜{away.losses}负</span>
          </Link>

          <div className="text-center">
            {game.status === "upcoming" ? (
              <span className="font-heading text-3xl font-bold text-foreground-400">VS</span>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <span className={`font-heading text-4xl md:text-5xl font-bold ${game.status === "live" ? "text-accent-600" : "text-foreground-950"}`}>{game.awayScore}</span>
                <span className="text-foreground-300 text-2xl">-</span>
                <span className={`font-heading text-4xl md:text-5xl font-bold ${game.status === "live" ? "text-accent-600" : "text-foreground-950"}`}>{game.homeScore}</span>
              </div>
            )}
            <p className="mt-2 text-xs text-foreground-500">
              {game.status === "live" ? `${game.quarter} ${game.clock}` : game.status === "final" ? "全场结束" : `${formatFullDate(game.date)} ${game.time} 开赛`}
            </p>
          </div>

          <Link to={`/team/${home.id}`} className="flex flex-col items-center gap-2 cursor-pointer group">
            <TeamBadge team={home} size="lg" />
            <span className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-700 text-center">{home.fullName}</span>
            <span className="text-xs text-foreground-500">主场 · {home.wins}胜{home.losses}负</span>
          </Link>
        </div>
      </div>

      {/* 数据标签页 */}
      <div className="mb-4 flex flex-wrap gap-1 rounded-md bg-background-100 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === tab.key ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-600 hover:bg-background-200"
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mb-12">
        {activeTab === "chart" && <ScoreChart game={game} />}
        {activeTab === "stats" && <StatsCompare game={game} />}
        {activeTab === "roster" && <RosterPanel game={game} />}
        {activeTab === "timeline" && <TimelineFeed game={game} />}
      </div>

      {/* 相关赛事推荐 */}
      <section>
        <h2 className="mb-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">相关赛事推荐</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {related.map((item) => (
            <GameCard key={item.id} game={item} />
          ))}
        </div>
      </section>
    </div>
  );
}